import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  CssBaseline,
  Alert,
} from '@mui/material';
import { styled } from '@mui/system';
import BeatLoader from 'react-spinners/BeatLoader';
import Mina from '../../assets/images/logo.png';
import '../../styles/Login.css';
import { API_URL } from '../../app/constants'

const MainContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily: 'Manrope, sans-serif',
  padding: '20px',
  '@media (min-width: 769px)': {
    alignItems: 'flex-start',
    paddingTop: '120px',
  },
  '@media (max-width: 768px)': {
    alignItems: 'flex-start',
    paddingTop: '120px',
    padding: '120px 20px 40px',
  },
});

const StyledContainer = styled(Container)({
  maxWidth: '700px',
  width: '700px',
  fontFamily: 'Manrope, sans-serif',
});

const StyledTextField = styled(TextField)({
  width: '100%',
  marginBottom: '16px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s',
    '& fieldset': {
      border: '1px solid #E5E7EB',
      borderRadius: '4px',
    },
    '&:hover': {
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#D1D5DB',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 0 0 2px rgba(147, 51, 234, 0.1)',
      '& fieldset': {
        borderColor: '#9333EA',
        borderWidth: '1px',
      },
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 14px',
    fontSize: '15px',
    height: '16px',
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
  },
});

const FieldTitle = styled(Typography)({
  textAlign: 'left',
  marginBottom: '4px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#374151',
  fontFamily: 'Manrope, sans-serif',
});

const StyledButton = styled(Button)({
  width: '100%',
  marginTop: '1rem',
  marginBottom: '1rem',
  backgroundColor: '#9333EA',
  color: 'white',
  borderRadius: '4px !important',
  border: '1px solid #9333EA',
  padding: '12px 14px',
  fontWeight: 600,
  fontSize: '15px',
  textTransform: 'none',
  letterSpacing: 0,
  fontFamily: 'Manrope, sans-serif',
  minHeight: '48px',
  boxShadow: 'none !important',
  '&.MuiButton-root': {
    borderRadius: '4px !important',
  },
  '&:hover': {
    backgroundColor: '#7928CA',
    border: '1px solid #7928CA',
    boxShadow: 'none !important',
    borderRadius: '4px !important',
  },
  '&.Mui-disabled': {
    backgroundColor: '#E9D5FF',
    color: '#fff',
    border: '1px solid #E9D5FF',
    borderRadius: '4px !important',
  },
});

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const data = new FormData(event.currentTarget);
    const email = data.get('email');

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to request password reset');
      }

      setSuccessMessage('Password reset request sent. Please check your email.');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message);
      setSuccessMessage('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <Box className="logo-container-box">
        <img src={Mina} alt="Mina Logo" className="mina-logo" />
      </Box>
      <MainContainer>
        <StyledContainer component="main">
          <Typography 
            component="h1" 
            variant="h4" 
            sx={{ 
              textAlign: 'center',
              fontWeight: 700, 
              fontSize: '1.8rem', 
              color: '#111827',
              marginBottom: '32px',
              fontFamily: 'Manrope, sans-serif'
            }}
          >
            Reset Password
          </Typography>
          <Box sx={{ textAlign: 'center', marginBottom: '64px' }}>
            <Typography 
              sx={{ 
                color: '#64748b',
                fontSize: '1rem',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
          </Box>
          {errorMessage && (
            <Alert 
              severity="error" 
              sx={{ 
                width: '100%', 
                marginBottom: 3,
                borderRadius: '4px',
                '& .MuiAlert-icon': {
                  color: '#F87171'
                },
                '& .MuiAlert-message': {
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: '14px'
                }
              }}
            >
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert 
              severity="success" 
              sx={{ 
                width: '100%', 
                marginBottom: 3,
                borderRadius: '4px',
                '& .MuiAlert-icon': {
                  color: '#22C55E'
                },
                '& .MuiAlert-message': {
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: '14px'
                }
              }}
            >
              {successMessage}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            <FieldTitle>Email Address</FieldTitle>
            <StyledTextField
              required
              fullWidth
              id="email"
              name="email"
              autoComplete="email"
              autoFocus
              placeholder="e.g. john@example.com"
            />
            
            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              disableElevation
            >
              {loading ? <BeatLoader color="#fff" size={10} /> : 'Send Reset Link'}
            </StyledButton>
          </Box>
        </StyledContainer>
      </MainContainer>
    </>
  );
}
